import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronDown } from '../../icons';
import { fetchJobGrades, type JobGradeDto } from '../../api/hr-job-grades';
import './JobGradeSelect.css';
import { registerSessionCache } from '../../lib/session-caches';

/**
 * JobGradeSelect (2026-08-28 · 자리 재정의 판 2) — 직위를 **고르게** 한다.
 *
 * 마스터(`hr_job_grades`, m138)는 2026-08-27 부터 있는데 아무도 안 골랐다 — 직위를 적는 자리
 * 일곱이 **전부 자유 입력**이라, 같은 직위가 사람마다 다른 글자로 저장되고 「직위=마스터에서
 * 고름 · 직급=직위가 정함」이라는 규칙이 화면 어디에도 없었다.
 *
 * ★★ **목록 밖 값을 막지 않는다.** 운영에는 이미 목록에 없는 직위로 저장된 사람이 있고,
 *    막으면 그 사람은 **저장할 때마다 400** 이 된다. 그래서 지금 값을 첫 항목으로 그대로 얹고
 *    「목록에 없음」이라고 알리기만 한다 — 담당자에게는 「목록에 담기」 길을 함께 준다.
 *
 * ★★ **못 읽으면 자유 입력으로 돌아간다.** `GET /hr/job-grades` 는 담당자 전용(adminOnly)이라
 *    본인 화면(정정 요청)에서는 403 이고, 옛 서버에는 404 다. 그때 칸을 잠그면 **쓰던 화면이
 *    멎는다** — 지금까지와 똑같은 `<input>` 을 그린다. 잃는 것은 «고르기» 뿐이다.
 *
 * 스타일은 **부르는 쪽 것을 그대로 쓴다** — `className` 으로 받은 클래스를 컨트롤에 얹는다
 * (`.hrcert__field input|select` · `.hra__field input|select` · `select.um-inp`).
 */

/** 마스터는 한 번만 부른다 — 한 화면에 이 컨트롤이 둘 이상 뜬다(입사 처리 · 상세). */
let cache: Promise<JobGradeDto[] | null> | null = null;

export function _resetJobGradeCacheForTests(): void { cache = null; }
// ★ 로그아웃하면 비운다 (2026-08-31) — 직위 목록은 **회사마다 다르다**(테넌트 경계).
registerSessionCache(() => { cache = null; });

function loadGrades(): Promise<JobGradeDto[] | null> {
  if (!cache) {
    cache = fetchJobGrades()
      .then((board) => board.grades)
      // 실패는 캐시하지 않는다 — 잠깐 끊긴 것이면 다음 화면에서 다시 물어야 한다.
      .catch(() => { cache = null; return null; });
  }
  return cache;
}

interface Props {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  /** 컨트롤에 얹을 클래스 — 화면마다 다른 입력 스타일을 그대로 물려받기 위함. */
  className?: string;
  disabled?: boolean;
  /** 목록에 없는 값일 때 「목록에 담기」 길을 함께 보인다 — 담당자 화면만 true. */
  manage?: boolean;
  /** `appearance:none` 인 select(예: `um-inp`)에 화살표를 그려 준다. */
  chevron?: boolean;
  /** 빈 값의 이름. */
  placeholder?: string;
  'aria-label'?: string;
}

export function JobGradeSelect({
  value, onChange, id, className, disabled, manage = false, chevron = false,
  placeholder = '직위 없음', 'aria-label': ariaLabel,
}: Props): JSX.Element {
  /** `undefined` = 읽는 중 · `null` = 못 읽음(자유 입력). */
  const [grades, setGrades] = useState<JobGradeDto[] | null | undefined>(undefined);
  /**
   * 한 번이라도 «목록 밖» 으로 만난 값들 — 계속 항목으로 남긴다.
   *
   * ★ 지금 값만 얹으면, 다른 직위를 골라 본 순간 원래 값이 목록에서 **사라져 되돌릴 수 없다**.
   *   잘못 눌렀을 때 돌아갈 자리를 남기는 것이 이 목록의 전부다.
   */
  const [seenUnlisted, setSeenUnlisted] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    void loadGrades().then((g) => { if (alive) setGrades(g); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!grades || value === '') return;
    if (grades.some((g) => g.name === value)) return;
    setSeenUnlisted((prev) => (prev.includes(value) ? prev : [...prev, value]));
  }, [grades, value]);

  // 읽는 중 · 못 읽음 → 지금까지와 같은 자유 입력. 값은 그대로 오간다.
  if (grades === undefined || grades === null) {
    return (
      <span className="jgs">
        <input
          id={id} className={className} value={value} disabled={disabled}
          aria-label={ariaLabel} maxLength={50}
          onChange={(e) => onChange(e.target.value)}
        />
      </span>
    );
  }

  const unlisted = value !== '' && !grades.some((g) => g.name === value);
  const outsiders = seenUnlisted.filter((n) => !grades.some((g) => g.name === n));
  // 접은 직위(active=false)는 새로 고르지 못하게 감추되, 지금 값이면 남긴다.
  const options = grades
    .filter((g) => g.active || g.name === value)
    .sort((a, b) => b.level - a.level);   // 높은 급이 위

  return (
    <span className={`jgs${chevron ? ' jgs--chev' : ''}`}>
      <select
        id={id} className={className} value={value} disabled={disabled} aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {/* 목록 밖 값은 위에 얹는다 — 안 얹으면 저장하는 순간 조용히 지워진다. */}
        {outsiders.map((n) => <option key={`x:${n}`} value={n}>{n} — 목록에 없음</option>)}
        {options.map((g) => (
          <option key={g.id} value={g.name}>
            {g.callName && g.callName !== g.name ? `${g.name} (${g.callName})` : g.name}
          </option>
        ))}
      </select>
      {chevron && <ChevronDown size={14} aria-hidden />}
      {unlisted && (
        <span className="jgs__flag">
          <AlertCircle size={12} aria-hidden />
          <span>목록에 없는 직위입니다</span>
          {manage && <Link className="jgs__link" to="/erp/hr/job-grades">목록에 담기</Link>}
        </span>
      )}
    </span>
  );
}
